import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { Member, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { Suggestion } from "../../lib/commentSuggestions.ts";
import { entitiesOf, entityOf } from "../../lib/entities.ts";
import { displayName, useSpacePeople } from "../../lib/people.ts";
import { titleOf } from "../../lib/titles.ts";
import { Avatar } from "../../ui/Avatar.tsx";
import { placePopover } from "../../ui/popover.ts";
import { SkinTone } from "../../ui/SkinTone.tsx";
import { ItemCard } from "../items/cards.tsx";
import { EntityLabel } from "../items/entityLabel.tsx";
import { KindIcon } from "../items/icons.tsx";

function MemberLine({ m }: { m: Member }) {
  return (
    <>
      <Avatar id={m.id} name={m.name} kind={m.kind} size={20} />
      <span class="min-w-0 flex-1 truncate font-medium">{displayName(m.id, m.name)}</span>
      <span class="shrink-0 truncate text-xs text-zinc-500">{m.email ?? t(`userKind.${m.kind}`)}</span>
    </>
  );
}

/** The highlighted item as its card shows it: picture, kind, title, issues, first fields, dates. */
function Preview({ space, s }: { space: SpaceOut; s: Suggestion | undefined }) {
  const entities = entitiesOf(space) ?? [];
  const members = useSpacePeople(space);
  if (s?.sigil !== "#" || !s.row) {
    return null;
  }
  return (
    <div class="hidden w-80 shrink-0 shadow-lg md:block">
      <ItemCard
        space={space}
        row={s.row}
        entity={entityOf(space, s.item.entity)}
        entities={entities}
        members={members}
      />
    </div>
  );
}

/** The skin tone for emoji, in a tiny card beside the hovered one (centered on its row, `top` being the row's middle). */
function ToneBar({ top }: { top: number }) {
  return (
    // Beside the hovered row; the padding bridges the gap, so moving onto it doesn't leave the list.
    <div class="absolute left-full z-10 -translate-y-1/2 pl-0.5" style={{ top: `${top}px` }}>
      <div class="rounded-md border border-zinc-200 bg-white p-0.5 shadow-lg">
        <SkinTone align="left" keepFocus={true} />
      </div>
    </div>
  );
}

/** A suggestion's key in the list. */
const keyOf = (s: Suggestion) => (s.sigil === "@" ? s.member.id : s.sigil === "#" ? s.item.id : s.emoji.u);

/**
 * Suggestions for an `@` or `#` being typed: members (who they are) or items as lists show them (kind or file icon,
 * title, entity), the highlighted item previewed beside.
 */
export function Suggestions({
  space,
  id,
  options,
  active,
  onPick,
  onHover,
  anchor,
}: {
  space: SpaceOut;
  id: string;
  /** What it opens by (the text box): over everything (a panel's edge doesn't cut it), below it or above. */
  anchor: HTMLElement | null;
  /** Null while searching. */
  options: readonly Suggestion[] | null;
  active: number;
  onPick: (s: Suggestion) => void;
  onHover: (i: number) => void;
}) {
  // Items keep one width (their titles vary while typing); people and emoji fit their names.
  const items = options?.[0]?.sigil === "#";
  // The tone choice beside the emoji the pointer is on, when it comes in tones (hands, people).
  const [hover, setHover] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  // In the top layer, placed by the box each time it changes (its height follows what's found).
  useLayoutEffect(() => {
    const el: HTMLDivElement | null = box.current;
    if (el === null || anchor === null) {
      return;
    }
    if (!el.matches(":popover-open")) {
      el.showPopover();
    }
    placePopover(el, anchor);
  });
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only hides the pointer's tone card (keyboards have the picker's).
    <div
      ref={box}
      popover="manual"
      class="fixed m-0 items-start gap-2 overflow-visible border-0 bg-transparent p-0 [&:popover-open]:flex"
      onMouseLeave={() => setHover(null)}
    >
      <div
        class={`flex max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border border-zinc-200 bg-white text-sm shadow-lg ${items ? "w-[32rem]" : "w-max min-w-48"}`}
      >
        <div id={id} role="listbox" aria-label={t("comments.suggestions")} class="max-h-80 overflow-auto py-1">
          {options === null ? <p class="px-3 py-1.5 text-zinc-500">{t("common.loading")}</p> : null}
          {options?.length === 0 ? <p class="px-3 py-1.5 text-zinc-500">{t("common.noMatches")}</p> : null}
          {options?.map((s, i) => (
            <div
              key={keyOf(s)}
              id={`${id}-${i}`}
              role="option"
              tabIndex={-1}
              aria-selected={i === active}
              ref={(el) => {
                if (i === active) {
                  el?.scrollIntoView({ block: "nearest" });
                }
              }}
              class="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-zinc-800 aria-selected:bg-blue-50"
              onMouseEnter={(e) => {
                onHover(i);
                const toned = s.sigil === ":" && (s.emoji.tones?.length ?? 0) > 0;
                const at = box.current?.getBoundingClientRect().top ?? 0;
                const row = e.currentTarget.getBoundingClientRect();
                // The row's middle: the card centers on it.
                setHover(toned ? row.top + row.height / 2 - at : null);
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the box
                onPick(s);
              }}
            >
              {s.sigil === "@" ? (
                <MemberLine m={s.member} />
              ) : s.sigil === ":" ? (
                <>
                  <span class="text-lg leading-none">{s.emoji.u}</span>
                  <span class="min-w-0 flex-1 truncate text-zinc-600">{s.emoji.label}</span>
                </>
              ) : (
                <>
                  <KindIcon item={s.item} />
                  <span class="min-w-0 flex-1 truncate">
                    {titleOf(s.item)}
                    {s.item.attached_to ? (
                      <span class="ml-2 text-xs text-zinc-500">
                        {t("comments.attachedTo", { title: titleOf(s.item.attached_to) })}
                      </span>
                    ) : null}
                  </span>
                  <span class="shrink-0">
                    <EntityLabel entity={entityOf(space, s.item.entity)} />
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      {hover === null ? null : <ToneBar top={hover} />}
      <Preview space={space} s={options?.[active]} />
    </div>
  );
}
